/**
 * The shared, read-only claim viewer.
 *
 * The same viewer is opened by a requester, a business approver, Finance and
 * Finance. It deliberately knows nothing about who may act on a claim — stage
 * actions live in the queue pages, so the workflow rules are not duplicated
 * into a component that five different roles render.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import {
  conversionNote,
  fmtDate,
  fmtDateTime,
  money,
  periodLabel,
  statusLabel,
  type AmountHistoryEntry,
  type ApprovalStep,
  type Claim,
  type ClaimRow,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { toDataUrl } from './imageBytes';
import { loadSignatureSrc, SignatureMark } from './SignatureMark';
import { DataTable, type Column } from './DataTable';
import { Modal } from './Modal';
import { LoadingBlock, Spinner } from './Spinner';
import { useToast } from './Toast';

/** userId → display name, assembled by the caller from what it already knows. */
export type NameMap = Readonly<Record<string, string>>;

export interface ClaimViewerProps {
  claimId: string;
  onClose: () => void;
  /**
   * `claims.get` returns user ids only, and there is no directory call a
   * non-admin may make, so names have to come from the caller's own queue rows.
   * Anything unresolved falls back to the raw employee id rather than "—", so
   * the reader can still tell two different approvers apart.
   */
  names?: NameMap;
  /** Convenience for the common case: the requester's name from a queue row. */
  requesterName?: string | null;
  /**
   * Show Finance's description editor.
   *
   * Set only by the Finance Review page, and only for a claim this user has
   * taken. The server re-checks: the flag decides whether the panel is drawn,
   * never whether the edit is allowed.
   */
  canEditDescriptions?: boolean;
}

type ReceiptRow = ClaimRow & { imageKey: string };

interface ViewUrlResult {
  url: string;
  expiresIn: number;
}

interface CachedUrl {
  url: string;
  expiresAt: number;
}

/**
 * VAT on a Philippine official receipt is inclusive: the printed amount already
 * contains the 12%, so it is extracted rather than added.
 */
function computeRow(row: ClaimRow): { vat: number; net: number; total: number } {
  const amount = Number(row.amount) || 0;
  const vat = row.vatStatus === 'Yes' ? amount - amount / 1.12 : 0;
  return { vat, net: amount - vat, total: amount };
}

/**
 * A person's name, or a dash.
 *
 * Never the user id. This used to fall back to `userId`, which put a raw uuid
 * on the claim form under "Employee Name" and "Prepared By" — and into the PDF
 * that goes to Finance. An unresolved name is a gap in a document; an id is
 * noise that reads like corruption.
 */
function nameOf(names: NameMap, userId: string | null | undefined): string {
  if (!userId) return '—';
  return names[userId] ?? '—';
}

function decisionLabel(step: ApprovalStep): string {
  if (!step.decision) return 'Awaiting';
  return `${step.decision} · ${fmtDate(step.ts)}`;
}

/* ------------------------------------------------------------------ */
/* Printable snapshot                                                  */
/* ------------------------------------------------------------------ */

function ClaimSnapshot({
  claim,
  names,
  signatures = {},
}: {
  claim: Claim;
  names: NameMap;
  /** Signature images by user id, for the people who have signed. */
  signatures?: Readonly<Record<string, string>>;
}): ReactNode {
  const totals = claim.rows.reduce(
    (acc, row) => {
      const { vat, net, total } = computeRow(row);
      return { vat: acc.vat + vat, net: acc.net + net, total: acc.total + total };
    },
    { vat: 0, net: 0, total: 0 },
  );

  const showFinance =
    claim.financeReviewedAmount != null ||
    claim.finalApprovedAmount != null ||
    claim.releasedAmount != null;

  return (
    <div className="claim-print-root">
      <div className="cf-header">
        <div className="company-block">
          <div className="company-name">GREAT DEALS E-COMMERCE CORP.</div>
          <div className="company-addr">
            2nd Floor Bookman Building, #373 Quezon Avenue St., Brgy. Lourdes, Quezon City, 1114
            <br />
            (02) 251-1258 &nbsp;•&nbsp; TIN: 009-717-682-000
          </div>
        </div>
        <div className="cf-title">
          <h2>EXPENSE CLAIM FORM</h2>
          <span className="rid">{claim.id}</span>
          <span className="badge2">{periodLabel(claim.period).toUpperCase()}</span>
        </div>
      </div>

      <div className="cf-meta">
        <div className="c">
          <label>Employee Name</label>
          <div className="v">{nameOf(names, claim.requesterId)}</div>
        </div>
        <div className="c">
          <label>Claim Period</label>
          <div className="v">{periodLabel(claim.period)}</div>
        </div>
        <div className="c">
          <label>Date Submitted</label>
          <div className="v">{fmtDate(claim.createdAt)}</div>
        </div>
        <div className="c">
          <label>Status</label>
          <div className="v">{statusLabel(claim.status)}</div>
        </div>
      </div>

      <table className="cf-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Receipt No.</th>
            <th>Company</th>
            <th>TIN</th>
            <th>Description</th>
            <th>Amount</th>
            <th>VAT?</th>
            <th>VAT</th>
            <th>Net of VAT</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {claim.rows.length === 0 ? (
            <tr>
              <td colSpan={10} style={{ padding: 14, color: 'var(--muted)' }}>
                No expenses recorded.
              </td>
            </tr>
          ) : (
            claim.rows.map((row) => {
              const { vat, net, total } = computeRow(row);
              return (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>{row.receipt}</td>
                  <td className="tl">{row.company}</td>
                  <td>{row.tin}</td>
                  <td className="tl">{row.description}</td>
                  <td>
                    {money(row.amount)}
                    {/* An approver signing off a converted amount must be able
                        to see what it was converted from and at what rate,
                        without opening anything. */}
                    {conversionNote(row.originalAmount, row.currency, row.fxRate) ? (
                      <span className="cf-fx">
                        {conversionNote(row.originalAmount, row.currency, row.fxRate)}
                      </span>
                    ) : null}
                  </td>
                  <td className={row.vatStatus === 'Yes' ? 'cf-vat-yes' : 'cf-vat-no'}>
                    {row.vatStatus}
                  </td>
                  <td>{money(vat)}</td>
                  <td>{money(net)}</td>
                  <td>{money(total)}</td>
                </tr>
              );
            })
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="tl2">
              TOTAL AMOUNT
            </td>
            <td>{money(totals.total)}</td>
            <td />
            <td>{money(totals.vat)}</td>
            <td>{money(totals.net)}</td>
            <td>{money(totals.total)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="cf-approval">
        <h4>Approval History</h4>
        <div className="cf-step-grid">
          <div className="cf-step">
            <div className="role">Prepared By</div>
            <div className="sig">
              <SignatureMark
                src={signatures[claim.requesterId] ?? null}
                name={nameOf(names, claim.requesterId)}
              />
              {nameOf(names, claim.requesterId)}
            </div>
            <div className="dt">{fmtDate(claim.createdAt)}</div>
          </div>
          {claim.approvalChain.map((step, i) => (
            <div className="cf-step" key={`${step.role}-${step.userId}-${i}`}>
              <div className="role">{step.role}</div>
              <div className="sig">{step.decision ? nameOf(names, step.userId) : ' '}</div>
              <div className="dt">{decisionLabel(step)}</div>
            </div>
          ))}
        </div>
        {claim.status === 'Disapproved' ? (
          <p style={{ fontSize: 11.5, color: 'var(--bad)', marginTop: 10 }}>
            <b>Disapproval reason:</b> {claim.disapprovalReason}
          </p>
        ) : null}
      </div>

      {showFinance ? (
        <div className="cf-finance-box">
          <h4>Finance</h4>
          <AmountLadder claim={claim} />
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {claim.financeReviewerId ? (
              <>
                Reviewed by <b>{nameOf(names, claim.financeReviewerId)}</b>
                {claim.financeReviewRemark ? ` — ${claim.financeReviewRemark}` : ''}
              </>
            ) : null}
            {claim.financeApproverId ? (
              <> &nbsp;·&nbsp; Approved by <b>{nameOf(names, claim.financeApproverId)}</b></>
            ) : null}
            {claim.financeApprovalDecision === 'Disapproved' ? (
              <>
                {' '}
                &nbsp;·&nbsp;
                <span style={{ color: 'var(--bad)' }}>
                  Returned by Finance: {claim.financeApprovalReason}
                </span>
              </>
            ) : null}
            {claim.treasuryUserId ? (
              <> &nbsp;·&nbsp; Released by <b>{nameOf(names, claim.treasuryUserId)}</b></>
            ) : null}
            {claim.releaseDate ? (
              <>
                {' '}
                on {fmtDate(claim.releaseDate)} (ref: {claim.paymentReference},{' '}
                {claim.paymentStatus})
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The amount ladder: claimed → reviewed → released.
 *
 * Round 2 removed the Final Approved rung — Finance's reviewed amount is the
 * approved amount now. Claims filed before that still carry one, so the rung
 * is shown when a claim has it and omitted when it does not, rather than
 * printing a permanent "—" on every claim from here on or quietly dropping a
 * figure that a historic claim was actually released against.
 */
export function AmountLadder({ claim }: { claim: Claim }): ReactNode {
  const showApproved = claim.finalApprovedAmount != null;
  return (
    <div className={showApproved ? 'amount-compare four' : 'amount-compare'}>
      <div className="amount-box claimed">
        <div className="l">Claimed</div>
        <div className="v">{money(claim.claimedAmount)}</div>
      </div>
      <div className="amount-box approved">
        <div className="l">Finance Reviewed &amp; Approved</div>
        <div className="v">
          {claim.financeReviewedAmount != null ? money(claim.financeReviewedAmount) : '—'}
        </div>
      </div>
      {showApproved ? (
        <div className="amount-box approved">
          <div className="l">Final Approved (retired stage)</div>
          <div className="v">{money(claim.finalApprovedAmount)}</div>
        </div>
      ) : null}
      <div className="amount-box released">
        <div className="l">Released</div>
        <div className="v">{claim.releasedAmount != null ? money(claim.releasedAmount) : '—'}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Receipt images                                                      */
/* ------------------------------------------------------------------ */

/**
 * Presigned receipt URLs expire (~15 minutes), which in a long-lived modal is
 * long enough to matter. Rather than showing a broken image we track when each
 * URL goes stale and re-mint it — proactively before an export, and reactively
 * if the browser reports a load failure anyway.
 */
function useReceiptUrls(
  claimId: string,
  keys: readonly string[],
  enabled: boolean,
): {
  urls: Readonly<Record<string, CachedUrl>>;
  error: string | null;
  loading: boolean;
  reload: (key: string) => Promise<string | null>;
  ensureFresh: (key: string) => Promise<string | null>;
} {
  const [urls, setUrls] = useState<Readonly<Record<string, CachedUrl>>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(new Map<string, Promise<string | null>>());
  const latest = useRef<Readonly<Record<string, CachedUrl>>>({});
  latest.current = urls;

  const reload = useCallback(
    (key: string): Promise<string | null> => {
      const running = inFlight.current.get(key);
      if (running) return running;

      const request = (async (): Promise<string | null> => {
        try {
          const result = await api.receipts.viewUrl<ViewUrlResult>({ key, claimId });
          // Expire a minute early so a URL never dies mid-render or mid-export.
          const expiresAt = Date.now() + Math.max(30, result.expiresIn - 60) * 1000;
          setUrls((prev) => ({ ...prev, [key]: { url: result.url, expiresAt } }));
          setError(null);
          return result.url;
        } catch (err) {
          setError(errorMessage(err));
          return null;
        } finally {
          inFlight.current.delete(key);
        }
      })();

      inFlight.current.set(key, request);
      return request;
    },
    [claimId],
  );

  const ensureFresh = useCallback(
    (key: string): Promise<string | null> => {
      const cached = latest.current[key];
      if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.url);
      return reload(key);
    },
    [reload],
  );

  useEffect(() => {
    if (!enabled || keys.length === 0) return;
    let cancelled = false;
    setLoading(true);
    void Promise.all(keys.map((key) => ensureFresh(key))).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, keys, ensureFresh]);

  return { urls, error, loading, reload, ensureFresh };
}

function ReceiptImage({
  src,
  alt,
  className,
  onExpired,
}: {
  src: string | undefined;
  alt: string;
  className?: string;
  onExpired: () => void;
}): ReactNode {
  const retriedFor = useRef<string | null>(null);

  if (!src) {
    return (
      <div className={className} style={{ display: 'grid', placeItems: 'center', minHeight: 110 }}>
        <Spinner />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        // One retry per URL: a 403 here almost always means the signature aged
        // out, and re-minting fixes it. Looping on a genuinely missing object
        // would hammer the API instead.
        if (retriedFor.current === src) return;
        retriedFor.current = src;
        onExpired();
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Export helpers                                                      */
/* ------------------------------------------------------------------ */

async function waitForImages(node: HTMLElement | null): Promise<void> {
  if (!node) return;
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const done = (): void => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }),
    ),
  );
}

function imageFormat(dataUrl: string): string {
  return dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
}

/* ------------------------------------------------------------------ */
/* Viewer                                                              */
/* ------------------------------------------------------------------ */

/**
 * The signature of each person who has signed this claim.
 *
 * Only people who have actually acted are asked for: an approver who has not
 * decided yet has signed nothing, and showing their mark on a pending step
 * would put a signature against a decision nobody has made.
 */
function useClaimSignatures(claim: Claim | null): Readonly<Record<string, string>> {
  const [signatures, setSignatures] = useState<Readonly<Record<string, string>>>({});

  const signedIds = claim
    ? [claim.requesterId, ...claim.approvalChain.filter((s) => s.decision).map((s) => s.userId)]
    : [];
  const key = signedIds.join(',');

  useEffect(() => {
    if (!claim || signedIds.length === 0) return undefined;
    let cancelled = false;
    void (async () => {
      const found: Record<string, string> = {};
      await Promise.all(
        signedIds.map(async (userId) => {
          const src = await loadSignatureSrc(() =>
            api.signature.forClaim({ claimId: claim.id, userId }),
          );
          if (src) found[userId] = src;
        }),
      );
      if (!cancelled) setSignatures(found);
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on who has signed, so a decision landing adds that signature.
  }, [claim?.id, key]);

  return signatures;
}

/** What `claims.get` actually returns: the claim, plus names for its people. */
type LoadedClaim = Claim & { participantNames?: Record<string, string> };

export function ClaimViewer({
  claimId,
  onClose,
  names,
  requesterName,
  canEditDescriptions = false,
}: ClaimViewerProps): ReactNode {
  const { toast } = useToast();
  const [claim, setClaim] = useState<LoadedClaim | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [lightbox, setLightbox] = useState<ReceiptRow | null>(null);
  const [exporting, setExporting] = useState<'print' | 'pdf' | null>(null);
  const [exportUrls, setExportUrls] = useState<Readonly<Record<string, string>> | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const signatures = useClaimSignatures(claim);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.claims.get<LoadedClaim>({ claimId });
        if (!cancelled) setClaim(loaded);
      } catch (err) {
        if (!cancelled) setLoadError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [claimId]);

  // The server's map wins. What the page passed down is a hint that arrives
  // first and covers the header while the claim is still loading; `claims.get`
  // resolves every participant, including approvers and the treasury releaser,
  // which no caller had to hand.
  const resolvedNames = useMemo<NameMap>(() => {
    const base: Record<string, string> = { ...names };
    if (claim && requesterName && !base[claim.requesterId]) {
      base[claim.requesterId] = requesterName;
    }
    return { ...base, ...(claim?.participantNames ?? {}) };
  }, [names, requesterName, claim]);

  const receipts = useMemo<ReceiptRow[]>(
    () =>
      (claim?.rows ?? []).filter((row): row is ReceiptRow => typeof row.imageKey === 'string'),
    [claim],
  );
  const receiptKeys = useMemo(() => receipts.map((r) => r.imageKey), [receipts]);

  const wantsUrls = galleryOpen || exporting !== null;
  const { urls, error: urlError, loading: urlsLoading, reload, ensureFresh } = useReceiptUrls(
    claimId,
    receiptKeys,
    wantsUrls,
  );

  const runExport = useCallback(
    async (mode: 'print' | 'pdf'): Promise<void> => {
      if (!claim) return;
      setExporting(mode);
      try {
        const resolved: Record<string, string> = {};
        for (const key of receiptKeys) {
          const url = await ensureFresh(key);
          if (url) resolved[key] = url;
        }
        // Commit the portal synchronously so the nodes exist before we measure
        // or rasterise them.
        flushSync(() => setExportUrls(resolved));
        await waitForImages(portalRef.current);

        if (mode === 'print') {
          await new Promise<void>((resolve) => {
            const done = (): void => {
              window.removeEventListener('afterprint', done);
              clearTimeout(timer);
              resolve();
            };
            // Not every browser fires afterprint; don't leave the portal mounted.
            const timer = setTimeout(done, 60_000);
            window.addEventListener('afterprint', done);
            window.print();
          });
        } else {
          await buildPdf(claim, resolved);
        }
      } catch (err) {
        toast(errorMessage(err), 'err');
      } finally {
        setExporting(null);
        setExportUrls(null);
      }
    },
    [claim, receiptKeys, ensureFresh, toast],
  );

  const buildPdf = useCallback(
    async (target: Claim, resolved: Readonly<Record<string, string>>): Promise<void> => {
      const snapshot = portalRef.current?.querySelector<HTMLElement>('.claim-print-root');
      if (!snapshot) throw new Error('The claim form could not be prepared for export.');

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(snapshot, { scale: 2, backgroundColor: '#ffffff' });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 40;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      pdf.addImage(imgData, 'PNG', 20, 20, imgW, imgH);
      let heightLeft = imgH - (pageH - 40);
      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 20, heightLeft - imgH + 20, imgW, imgH);
        heightLeft -= pageH - 40;
      }

      let skipped = 0;
      for (const row of target.rows) {
        const url = row.imageKey ? resolved[row.imageKey] : undefined;
        if (!url) {
          if (row.imageKey) skipped += 1;
          continue;
        }
        const dataUrl = await toDataUrl(url);
        if (!dataUrl) {
          skipped += 1;
          continue;
        }
        pdf.addPage('a4', 'portrait');
        const pW = pdf.internal.pageSize.getWidth();
        const pH = pdf.internal.pageSize.getHeight();
        pdf.setFontSize(11);
        pdf.setTextColor(31, 58, 95);
        pdf.text(
          `${target.id}   ${row.date}   ${row.company}   ${row.receipt}   ${money(row.amount)}`,
          pW / 2,
          26,
          { align: 'center' },
        );
        const props = pdf.getImageProperties(dataUrl);
        const ratio = Math.min((pW - 80) / props.width, (pH - 80) / props.height);
        pdf.addImage(
          dataUrl,
          imageFormat(dataUrl),
          (pW - props.width * ratio) / 2,
          46,
          props.width * ratio,
          props.height * ratio,
        );
      }

      pdf.save(`${target.id}.pdf`);
      if (skipped > 0) {
        toast(
          `${skipped} receipt image(s) could not be embedded in the PDF. Use Print to include them.`,
          'err',
        );
      }
    },
    [toast],
  );

  const historyRows = useMemo(
    () => (claim?.amountHistory ?? []).map((entry, i) => ({ ...entry, key: String(i) })),
    [claim],
  );

  const historyColumns: ReadonlyArray<Column<AmountHistoryEntry & { key: string }>> = useMemo(
    () => [
      { key: 'field', header: 'Field', cell: (r) => r.field },
      { key: 'from', header: 'From', align: 'right', cell: (r) => (r.from == null ? '—' : money(r.from)) },
      { key: 'to', header: 'To', align: 'right', cell: (r) => money(r.to) },
      { key: 'user', header: 'Changed By', cell: (r) => r.user },
      { key: 'ts', header: 'When', cell: (r) => fmtDateTime(r.ts) },
      { key: 'reason', header: 'Reason', cell: (r) => r.reason || '—' },
    ],
    [],
  );

  const busy = exporting !== null;

  /**
   * Finance's one editable field.
   *
   * Drafts live in local state until Saved, so a half-typed correction is
   * never written, and the reloaded claim replaces the whole object — the
   * printable form above re-renders with the new text rather than showing the
   * old wording beside the new one.
   */
  const [descDrafts, setDescDrafts] = useState<Record<string, string>>({});
  const [savingRow, setSavingRow] = useState<string | null>(null);

  const saveDescription = useCallback(
    async (rowId: string, next: string): Promise<void> => {
      setSavingRow(rowId);
      try {
        const updated = await api.financeReview.editDescription<LoadedClaim>({
          claimId,
          rowId,
          description: next.trim(),
        });
        setClaim(updated);
        setDescDrafts((d) => {
          const { [rowId]: _saved, ...rest } = d;
          return rest;
        });
        toast('Description updated.', 'ok');
      } catch (err) {
        toast(errorMessage(err), 'err');
      } finally {
        setSavingRow(null);
      }
    },
    [claimId, toast],
  );

  const title = claim
    ? `${claim.id} — ${nameOf(resolvedNames, claim.requesterId)}`
    : claimId;

  return (
    <>
      <Modal title={title} onClose={onClose} wide>
        {loadError ? <div className="form-error" role="alert">{loadError}</div> : null}
        {!claim && !loadError ? <LoadingBlock label="Loading claim…" /> : null}

        {claim ? (
          <>
            <div className="pdf-toolbar">
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
                  aria-label="Zoom out"
                >
                  －
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setZoom((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10))}
                  aria-label="Zoom in"
                >
                  ＋
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setZoom(1)}>
                  Reset
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-gold btn-sm"
                  onClick={() => void runExport('pdf')}
                  disabled={busy}
                >
                  {exporting === 'pdf' ? <Spinner /> : '⬇'} Download PDF
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => void runExport('print')}
                  disabled={busy}
                >
                  {exporting === 'print' ? <Spinner /> : '🖨'} Print / Save as PDF
                </button>
              </div>
            </div>

            <div className="pdf-frame">
              <div
                className="pdf-page"
                style={{ width: 900, transform: `scale(${zoom})` }}
              >
                <ClaimSnapshot claim={claim} names={resolvedNames} signatures={signatures} />
              </div>
            </div>

            {canEditDescriptions ? (
              <div className="panel" style={{ marginTop: 14 }}>
                <h3>Descriptions</h3>
                <p className="hint tight">
                  Finance may correct the description on a line. Amounts, dates, receipt numbers
                  and attachments stay as the requestor filed them, and every change here is
                  recorded in the audit trail with the previous wording.
                </p>
                <ul className="desc-edit-list">
                  {claim.rows.map((row) => {
                    const draft = descDrafts[row.id] ?? row.description;
                    const dirty = draft.trim() !== row.description && draft.trim() !== '';
                    return (
                      <li key={row.id}>
                        <div className="desc-edit-meta">
                          <b>{row.receipt || 'No receipt no.'}</b>
                          <span>
                            {row.date} · {money(row.amount)}
                          </span>
                        </div>
                        <input
                          aria-label={`Description for receipt ${row.receipt || row.date}`}
                          value={draft}
                          maxLength={200}
                          onChange={(e) =>
                            setDescDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={!dirty || savingRow === row.id}
                          onClick={() => void saveDescription(row.id, draft)}
                        >
                          {savingRow === row.id ? <Spinner /> : 'Save'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <div className="panel" style={{ marginTop: 14 }}>
              <h3>Amounts</h3>
              <AmountLadder claim={claim} />
              <h3 style={{ marginTop: 16 }}>Amount History</h3>
              <DataTable
                rows={historyRows}
                columns={historyColumns}
                rowKey={(r) => r.key}
                empty={<p className="hint tight">No amount has been changed yet.</p>}
              />
            </div>

            <div className="panel">
              <h3>Approval Chain</h3>
              <ul className="chain-list">
                <li>
                  <b>Prepared By</b> — {nameOf(resolvedNames, claim.requesterId)}
                  <div className="meta">Submitted {fmtDateTime(claim.createdAt)}</div>
                </li>
                {claim.approvalChain.map((step, i) => (
                  <li key={`${step.role}-${step.userId}-${i}`}>
                    <b>{step.role}</b> — {nameOf(resolvedNames, step.userId)}
                    <div className="meta">
                      {step.decision
                        ? `${step.decision} · ${fmtDateTime(step.ts)}`
                        : 'Awaiting decision'}
                      {step.reason ? ` · ${step.reason}` : ''}
                    </div>
                  </li>
                ))}
                {claim.approvalChain.length === 0 ? (
                  <li>
                    <b>No business approval step</b>
                    <div className="meta">
                      Executive route — the claim went straight to Finance Review.
                    </div>
                  </li>
                ) : null}
              </ul>
            </div>

            <div className="panel">
              <h3>
                <span className="title">
                  Receipt Photos <span className="tag-chip">{receipts.length}</span>
                </span>
                {receipts.length > 0 ? (
                  <div className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setGalleryOpen((open) => !open)}
                    >
                      {galleryOpen ? 'Hide' : 'Show'}
                    </button>
                  </div>
                ) : null}
              </h3>
              {receipts.length === 0 ? (
                <p className="hint tight">
                  No receipt photos were attached to this claim (rows may have been added
                  manually).
                </p>
              ) : !galleryOpen ? (
                <p className="hint tight">
                  Photos load on demand — the view links are short-lived and minted per request.
                </p>
              ) : (
                <>
                  {urlError ? <div className="form-error" role="alert">{urlError}</div> : null}
                  {urlsLoading ? <LoadingBlock label="Preparing receipt links…" /> : null}
                  <div className="receipt-gallery">
                    {receipts.map((row) => (
                      <button
                        type="button"
                        className="receipt-thumb"
                        key={row.id}
                        onClick={() => setLightbox(row)}
                      >
                        <ReceiptImage
                          src={urls[row.imageKey]?.url}
                          alt={`Receipt from ${row.company} dated ${row.date}`}
                          onExpired={() => void reload(row.imageKey)}
                        />
                        <div className="cap">
                          {row.date} · {money(row.amount)}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <p className="hint tight">
              {canEditDescriptions
                ? 'Only the descriptions above are editable here. Set the amount from the review queue.'
                : 'This view is read-only. Approve, review or release the claim from its workflow queue.'}
            </p>
          </>
        ) : null}
      </Modal>

      {lightbox ? (
        <Modal
          title={`${lightbox.company} — ${lightbox.date}`}
          onClose={() => setLightbox(null)}
          wide
        >
          <ReceiptImage
            src={urls[lightbox.imageKey]?.url}
            alt={`Receipt from ${lightbox.company} dated ${lightbox.date}`}
            className="receipt-full"
            onExpired={() => void reload(lightbox.imageKey)}
          />
          <p className="hint" style={{ textAlign: 'center', marginTop: 10 }}>
            Receipt No. {lightbox.receipt || '—'} · {money(lightbox.amount)}
          </p>
        </Modal>
      ) : null}

      {claim && exportUrls
        ? createPortal(
            <div className="claim-print-portal" ref={portalRef}>
              <ClaimSnapshot claim={claim} names={resolvedNames} signatures={signatures} />
              {claim.rows.map((row) => {
                const url = row.imageKey ? exportUrls[row.imageKey] : undefined;
                if (!url) return null;
                return (
                  <div className="receipt-page" key={row.id}>
                    <div className="receipt-caption">
                      {claim.id} &nbsp;•&nbsp; {row.date} &nbsp;•&nbsp; {row.company} &nbsp;•&nbsp;{' '}
                      {row.receipt} &nbsp;•&nbsp; {money(row.amount)}
                    </div>
                    <img src={url} alt={`Receipt from ${row.company} dated ${row.date}`} />
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
